import { useEffect } from 'react';

const POINTER_EDGE_PADDING_PX = 24;

function clampPointerOffset(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

interface UseAdaptivePointerOffsetParams {
  anchorElement: HTMLSpanElement | null;
  panelElement: HTMLDivElement | null;
  hasPointer: boolean;
  isOpen: boolean;
  pointerEdge?: 'top' | 'bottom' | 'left' | 'right';
  size: 'M' | 'S';
}

export function useAdaptivePointerOffset({
  anchorElement,
  panelElement,
  hasPointer,
  isOpen,
  pointerEdge,
  size,
}: UseAdaptivePointerOffsetParams) {
  useEffect(() => {
    if (size !== 'S' || !hasPointer || !isOpen || !anchorElement || !panelElement || !pointerEdge) return;

    const pointerElement = panelElement.querySelector('[data-testid$="-pointer"]');

    if (!(pointerElement instanceof HTMLElement)) return;

    let frameId = 0;

    const updatePointerOffset = () => {
      const anchorRect = anchorElement.getBoundingClientRect();
      const panelRect = panelElement.getBoundingClientRect();
      const pointerRect = pointerElement.getBoundingClientRect();
      const pointerHalfWidth = pointerRect.width / 2;
      const pointerHalfHeight = pointerRect.height / 2;

      panelElement.style.removeProperty('--popover-pointer-offset-x');
      panelElement.style.removeProperty('--popover-pointer-offset-y');

      if (pointerEdge === 'top' || pointerEdge === 'bottom') {
        const desiredOffset = anchorRect.left + anchorRect.width / 2 - panelRect.left;
        const minOffset = POINTER_EDGE_PADDING_PX + pointerHalfWidth;
        const maxOffset = panelRect.width - POINTER_EDGE_PADDING_PX - pointerHalfWidth;

        panelElement.style.setProperty(
          '--popover-pointer-offset-x',
          `${clampPointerOffset(desiredOffset, minOffset, maxOffset)}px`,
        );

        return;
      }

      const desiredOffset = anchorRect.top + anchorRect.height / 2 - panelRect.top;
      const minOffset = POINTER_EDGE_PADDING_PX + pointerHalfHeight;
      const maxOffset = panelRect.height - POINTER_EDGE_PADDING_PX - pointerHalfHeight;

      panelElement.style.setProperty(
        '--popover-pointer-offset-y',
        `${clampPointerOffset(desiredOffset, minOffset, maxOffset)}px`,
      );
    };

    const schedulePointerOffsetUpdate = () => {
      if (frameId) return;

      frameId = window.requestAnimationFrame(() => {
        frameId = 0;
        updatePointerOffset();
      });
    };

    updatePointerOffset();
    window.addEventListener('resize', schedulePointerOffsetUpdate);
    window.addEventListener('scroll', schedulePointerOffsetUpdate, true);

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', schedulePointerOffsetUpdate);
      window.removeEventListener('scroll', schedulePointerOffsetUpdate, true);
    };
  }, [anchorElement, hasPointer, isOpen, panelElement, pointerEdge, size]);
}
